import * as Sentry from '@sentry/react'

import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { FC, useCallback, useMemo, useState } from 'react'
import { useAddFavoriteVisual, useDeleteFavoriteVisual, useGetAssignmentVisualByTypeName } from 'dataQueries'
import { useGalleryAdditionalVisuals, useGalleryAssignment, useGalleryVisualSelection, useGalleryVisualType, useGalleryVisuals, useGalleryVisualsDownloadArchive, useGalleryVisualsMeta } from '../../contexts'
import { useLocation, useNavigate, useParams } from 'react-router'

import { ClientGalleryFloatingToolBar } from '../ClientGalleryFloatingToolBar'
import { ClientGalleryPhotoSection } from '../ClientGalleryPhotoSection'
import { ClientPropertySelfUploadPopup } from 'components/pages/Property/ClientProperty/ClientPropertySelfUploadPopup/ClientPropertySelfUploadPopup.module'
import { Direction } from 'components/pages/VisualsEditing'
import { ExpandedVisualPopup } from 'components/pages/VisualsEditing/ExpandedVisualPopup'
import { GalleryUpsellPayment } from 'components/common/Gallery/GalleryUpsellPayment'
import { GalleryVisualDownloadPopup } from 'components/common/Gallery/GalleryVisualDownloadPopup'
import { Path } from 'constants/router'
import { VisualFileType } from 'constants/visual'
import classNames from 'classnames'
import { getPropertyPathname } from 'utils/helpers/getPropertyPathname'
import styles from './ClientGalleryPhoto.module.sass'
import { useClientGallery } from '../_main/contexts'
import { useDebouncedMemo } from 'utils/helpers'
import { useImageRegex } from 'utils/hooks'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'

enum UpdateFavoriteType {
  DELETE = 'DELETE',
  ADD = 'ADD',
}

/**
 * Client gallery photo content.
 * 
 * @example <ClientGalleryPhoto />
 */
export const ClientGalleryPhoto: FC = () => {

  const { pathname } = useLocation()
  const { id } = useParams<{ id: string }>()
  const { webType } = useGalleryVisualType()
  const { initiateArchiveDownload } = useGalleryVisualsDownloadArchive()
  const { t } = useTranslation(['gallery', 'visual_loading'])
  const navigate = useNavigate()

  const {
    downloadVisuals,
    downloadVisualsKeys,
    downloadVisualsCount,
    favoritedVisuals,
    visualMetadataMap,
    setFavoritedVisuals
  } = useGalleryVisuals()

  const {
    purchasedVisualsKeys,
    unPurchasedVisualsKeys,
    prepurchasedVisualsRemaining,
  } = useGalleryVisualsMeta()

  const {
    selected,
    selectedNotPurchasedVisualsKeys,
    toggleSelectOnImage,
  } = useGalleryVisualSelection()

  const { isAdditionalVisualPurchaseDisabled } = useGalleryAdditionalVisuals()

  const {
    assignmentId,
    setIsToolBarOpen,
  } = useClientGallery()

  const {
    logGalleryEvent,
  } = useGalleryAssignment()

  const { spawnErrorToast } = useSnackbar()

  const imageNameReplacePattern = useImageRegex(assignmentId)

  const addFavoriteVisual = useAddFavoriteVisual()
  const deleteFavoriteVisual = useDeleteFavoriteVisual()
  const assignmentVisualByName = useGetAssignmentVisualByTypeName()

  const [isExpandedVisualOpen, setIsExpandedVisualOpen] = useState<boolean>(false)
  const [expandedVisualName, setExpandedVisualName] = useState<string | undefined>(undefined)

  const isPropertyPathname = useMemo(() => getPropertyPathname(pathname), [pathname])

  const navigateToEditing = (
    file: File,
    imageUrl: string,
    isFileVersioned: boolean,
    isOldEditingTool: boolean
  ) => {
    if (!isOldEditingTool) {
      if (isPropertyPathname) {
        if (id && assignmentId) {
          navigate(Path.PROPERTY_ASSIGNMENT_AI_STUDIO.replace(':id', id.toString()).replace(':assignmentId', assignmentId.toString()), { state: { file, imageUrl, assignmentId, isFileVersioned } })
        }
      } else {
        navigate(Path.GALLERY_VISUAL_AI_STUDIO.replace(':id', assignmentId), {
          state: { file, imageUrl, assignmentId, isFileVersioned }
        })
      }
      // TODO remove this when the new AI Studio is released
    } else {
      if (isPropertyPathname) {
        if (id && assignmentId) {
          navigate(Path.PROPERTY_ASSIGNMENT_PINTURA_EDITING.replace(':id', id.toString()).replace(':assignmentId', assignmentId.toString()), { state: { file, imageUrl, assignmentId, isFileVersioned } })
        }
      } else {
        navigate(Path.GALLERY_VISUAL_PINTURA_EDITING.replace(':id', assignmentId), {
          state: { file, imageUrl, assignmentId, isFileVersioned }
        })
      }
    }

    logAnalyticsEvent(isOldEditingTool ? AnalyticsEvent.PINTURA_OPEN : AnalyticsEvent.AI_STUDIO_OPENED, {
      galleryType: 'CLIENT',
      assignmentId,
      sourceVisualFilename: file.name,
      imageUrl,
    })
  }

  const fetchImageUrl = useCallback(async (visualName: string): Promise<string> => {
    try {
      const response = await assignmentVisualByName.mutateAsync({
        assignmentId,
        visualType: VisualFileType.POST,
        visualName,
      })

      if (response.data.url) {
        return response.data.url
      }
    } catch (error) {
      console.error('Error during image loading:', error)

      const sentryMessage = 'Loading POST image for Pintura editing ERROR'
      Sentry.captureException(sentryMessage, scope => {
        scope.clearBreadcrumbs()
        scope.setFingerprint([sentryMessage])
        scope.setExtra('visualName', visualName)
        scope.setExtra('assignmentId', assignmentId)
        scope.setExtra('visualType', VisualFileType.POST)
        return scope
      })

      spawnErrorToast(t('visual_loading:general_error', { error }))
    }
    return ''

  }, [assignmentVisualByName, assignmentId, spawnErrorToast, t])

  // TODO remove this when the new AI Studio is released
  const onEditWithPinturaClick = useCallback(async (file: File, isFileVersioned: boolean) => {
    const imageUrl = await fetchImageUrl(file.name)

    navigateToEditing(file, imageUrl, isFileVersioned, true)
    // ignore the eslint warning for the dependency array - navigateToEditing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchImageUrl])

  const onOpenAIStudio = useCallback(async (file: File, isFileVersioned: boolean) => {
    const imageUrl = await fetchImageUrl(file.name)

    navigateToEditing(file, imageUrl, isFileVersioned, false)
    // ignore the eslint warning for the dependency array - navigateToEditing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchImageUrl])

  const onUpdateFavoriteVisuals = useDebouncedMemo(() =>
    (type: UpdateFavoriteType, filename: string) => {
      switch (type) {
        case UpdateFavoriteType.ADD:
          addFavoriteVisual.mutate({ assignmentId, visuals: [filename] }, {
            onError: () => {
              favoritedVisuals.delete(filename)
              setFavoritedVisuals(new Set(favoritedVisuals))
            }
          })
          break
        case UpdateFavoriteType.DELETE:
          deleteFavoriteVisual.mutate({ assignmentId, visuals: [filename] }, {
            onError: () => {
              favoritedVisuals.add(filename)
              setFavoritedVisuals(new Set(favoritedVisuals))
            }
          })
          break

        default:
          break
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    , [], 500, 700)

  const isFavoriteVisualsDisabled = addFavoriteVisual.isPending || deleteFavoriteVisual.isPending

  const toggleFavoriteOnImage = useCallback((filename?: string) => {
    if (!filename) return
    if (isFavoriteVisualsDisabled) return

    if (favoritedVisuals.has(filename)) {
      favoritedVisuals.delete(filename)
      setFavoritedVisuals(new Set(favoritedVisuals))
      onUpdateFavoriteVisuals(UpdateFavoriteType.DELETE, filename)
      logGalleryEvent(AnalyticsEvent.GALLERY_UNMARK_FAVORITE)
    } else {
      favoritedVisuals.add(filename)
      setFavoritedVisuals(new Set(favoritedVisuals))
      onUpdateFavoriteVisuals(UpdateFavoriteType.ADD, filename)
      logGalleryEvent(AnalyticsEvent.GALLERY_MARK_FAVORITE)
    }
  }, [favoritedVisuals, onUpdateFavoriteVisuals, logGalleryEvent, setFavoritedVisuals, isFavoriteVisualsDisabled])

  const switchExpandedImage = useCallback((direction: Direction) => {
    if (!expandedVisualName) return
    if (!downloadVisuals) return

    const isDirectionNext = direction === Direction.RIGHT

    if (downloadVisualsCount === 0) return
    let chooseThisOne = false

    for (let i = isDirectionNext ? 0 : downloadVisualsCount - 1; isDirectionNext ? i < downloadVisualsCount : i >= 0; isDirectionNext ? i++ : i--) {
      if (chooseThisOne) return setExpandedVisualName(downloadVisualsKeys[i])
      if (expandedVisualName === downloadVisualsKeys[i]) chooseThisOne = true
    }

    return setExpandedVisualName(isDirectionNext ? downloadVisualsKeys[0] : downloadVisualsKeys[downloadVisualsCount - 1])
  }, [expandedVisualName, downloadVisuals, downloadVisualsCount, downloadVisualsKeys])

  const expandedVisual = useMemo(() => {
    if (!expandedVisualName) return undefined
    if (!downloadVisuals?.[expandedVisualName]) return undefined
    return downloadVisuals[expandedVisualName]?.[webType]
  }, [downloadVisuals, expandedVisualName, webType])

  const expandedImageName = useMemo(() => expandedVisual?.file?.name, [expandedVisual?.file?.name])

  return (
    <div className={classNames(styles.clientGalleryPhoto)}>

      <ClientGalleryPhotoSection
        favorited={favoritedVisuals}
        setIsExpandedVisualOpen={setIsExpandedVisualOpen}
        setExpandedVisualName={setExpandedVisualName}
        toggleFavoriteOnImage={toggleFavoriteOnImage}
        isFavoriteDisabled={isFavoriteVisualsDisabled}
        visualsMetadata={visualMetadataMap}
        onEditWithPinturaClick={onEditWithPinturaClick}
        onOpenAIStudio={onOpenAIStudio}
      />

      <ClientGalleryFloatingToolBar />

      <GalleryVisualDownloadPopup />

      <GalleryUpsellPayment />

      <ExpandedVisualPopup
        isOpen={isExpandedVisualOpen}
        isSelectable={!(isAdditionalVisualPurchaseDisabled && unPurchasedVisualsKeys.has(expandedImageName ?? '') && !selectedNotPurchasedVisualsKeys.has(expandedImageName ?? '') && selectedNotPurchasedVisualsKeys.size >= prepurchasedVisualsRemaining)}
        isFavoriteSelectable
        isSelected={selected.has(expandedImageName ?? '')}
        isFavorite={favoritedVisuals.has(expandedImageName ?? '')}
        isPurchased={purchasedVisualsKeys.has(expandedImageName ?? '')}
        visualMetadata={visualMetadataMap[expandedImageName ?? ''] ?? null}
        signedUrl={expandedVisual?.signedUrl}
        filename={expandedImageName}
        label={expandedImageName?.replace(imageNameReplacePattern, '')}
        switchExpandedVisual={switchExpandedImage}
        toggleFavoriteOnImage={toggleFavoriteOnImage}
        onClose={() => setIsExpandedVisualOpen(false)}
        onSelect={() => {
          setIsToolBarOpen(true)
          toggleSelectOnImage(expandedImageName)
        }}
        onDonwload={() => initiateArchiveDownload(new Set<string>([expandedImageName ?? '']))}
        onEditWithPinturaClick={() => onEditWithPinturaClick(expandedVisual?.file ? expandedVisual.file : new File([], expandedVisualName ?? ''), visualMetadataMap[expandedVisual?.file?.name ?? ''].selfEdited)}
      />

      <ClientPropertySelfUploadPopup />

    </div>
  )
}
